import { useEffect, useRef } from "react";
import type { ImportPlan } from "../invoiceCsv.ts";
import { formatAmount } from "../format.ts";
import { Modal } from "./Modal.tsx";

type Props = {
  fileName: string;
  plan: ImportPlan;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ImportDialog({ fileName, plan, onConfirm, onCancel }: Props) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const count = plan.add.length;
  const sum = plan.add.reduce((a, r) => a + r.amount, 0);
  const flagged = plan.duplicates.filter((d) => d.changed.length > 0);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <Modal labelledBy="import-title" onClose={onCancel}>
      <h2 id="import-title">Importera fakturor</h2>
      <p className="modal-subtitle">{fileName}</p>

      <ul className="import-summary">
        <li>
          <strong>{count}</strong> nya rader ({formatAmount(sum)})
        </li>
        {plan.duplicates.length > 0 && (
          <li>
            <strong>{plan.duplicates.length}</strong> finns redan i Likvid och
            hoppas över
          </li>
        )}
        {plan.duplicatesInFile.length > 0 && (
          <li>
            <strong>{plan.duplicatesInFile.length}</strong> dubbletter i filen
            hoppas över
          </li>
        )}
        {plan.ignoredLines.length > 0 && (
          <li>
            <strong>{plan.ignoredLines.length}</strong> rad
            {plan.ignoredLines.length === 1 ? "" : "er"} utan faktura ignoreras
          </li>
        )}
      </ul>

      {count > 0 && (
        <div className="import-list">
          <table>
            <tbody>
              {plan.add.map((r) => (
                <tr key={r.id}>
                  <td className="import-date">{r.dueDate}</td>
                  <td className="import-supplier">{r.supplier}</td>
                  <td className="import-amount">{formatAmount(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {flagged.length > 0 && (
        <div className="import-flagged">
          <p>Redan i Likvid men med andra värden i filen (ändras inte):</p>
          <ul>
            {flagged.map((d) => (
              <li key={d.row.id}>
                {d.row.supplier} — {d.changed.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="modal-actions">
        <button type="button" onClick={onCancel}>
          Avbryt
        </button>
        <button
          ref={confirmRef}
          type="button"
          className="primary"
          disabled={count === 0}
          onClick={onConfirm}
        >
          {count === 0
            ? "Inget att importera"
            : `Lägg till ${count} rad${count === 1 ? "" : "er"}`}
        </button>
      </div>
    </Modal>
  );
}
